// SPDX-FileCopyrightText: © 2023 Schneider Electric
//
// SPDX-License-Identifier: Apache-2.0

import type { ComponentInterface } from "@stencil/core";
import { Component, Element, h, Listen, Prop, State } from "@stencil/core";

import { onMobileBreakpoint } from "../utils";

/**
 * @slot start - Content to display in the start slot of the header eyebrow.
 * @slot end - Content to display in the end slot of the header eyebrow.
 */

@Component({
  tag: "qds-web-header-eyebrow",
  shadow: true,
  styleUrl: "header-eyebrow.css",
})
export class HeaderEyebrow implements ComponentInterface {
  @Element() public el: HTMLElement;

  /**
   * If true, the eyebrow is always visible and does not get the is-scroll class.
   */
  @Prop() public alwaysVisible: boolean;

  @State() public isScroll = false;

  @State() public isMobile: boolean;

  private cleanupMobileListener?: () => void;

  @Listen("scroll", { target: "window" })
  public applyScrollBehavior(): void {
    const { scrollY } = window;
    const scrollThreshold = 5;
    this.isScroll = scrollY > scrollThreshold;
  }

  public componentWillLoad(): Promise<void> | void {
    this.cleanupMobileListener = onMobileBreakpoint((isMobile: boolean) => {
      this.isMobile = isMobile;
    }, this.el);
  }

  public disconnectedCallback(): void {
    this.cleanupMobileListener?.();
  }

  public render() {
    return (
      <div
        class={{
          mobile: this.isMobile,
          web: !this.isMobile,
          "is-scroll": !this.alwaysVisible && this.isScroll,
        }}
      >
        <div class="eyebrow">
          <div class="qds-eyebrow-item-gap">
            <div class="qds-eyebrow-item-gap">
              <slot name="start" />
            </div>
            <div class="qds-eyebrow-item-gap">
              <slot />
            </div>
          </div>
          <div class="qds-eyebrow-item-gap">
            <slot name="end" />
          </div>
        </div>
      </div>
    );
  }
}
